import i18n from 'i18next';
import { useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { BFF_ENDPOINTS, COPY, GOOGLE_ANALYTICS } from 'src/app/constants';
import { sendInputEvent } from 'src/app/lib/google-analytics';
import captureEvent from 'src/app/lib/gtm-functions';
import networkService from 'src/app/lib/network-service';
import {
  accessTokenAtom,
  checkExerciseAtom,
  conversationsAtom,
  feedbackAtom,
  frameParamsAtom,
  selectedLangAtom,
} from 'src/app/state/atoms';
import { getRuntimeEnvironment } from 'src/app/utils/getRuntimeEnvironment';
import { isTelemetryEnabled } from 'src/app/utils/getTelemetrySwitch';
import { prepareConversationPayload } from 'src/app/utils/prepareConversationPayload';
import withFeedbackMapper from 'src/app/utils/withFeedbackMapper';
import { IConversations } from 'src/types/serviceResponseTypes';

export type TUserInputType = 'multiple_choice' | 'single_input';

const studentTracker = {
  currentQuestionId: null,
  maxAttempts: 2,
  attemptsLeft: 2,
  attemptsTried: 1,

  startNewQuestion(questionId: any) {
    this.currentQuestionId = questionId;
    this.attemptsLeft = this.maxAttempts;
    this.attemptsTried = 1;
  },

  attemptQuestion(responseAttempt: any) {
    this.attemptsLeft = responseAttempt;
    this.attemptsTried = this.maxAttempts - this.attemptsLeft;
  },

  getAttemptsTried() {
    return this.attemptsTried;
  },
};

/**
 * Network hook to fetch Tutor responses
 */
function useSendConversation() {
  const [conversations, setConversations] = useRecoilState(conversationsAtom);
  const [feedbackData] = useRecoilState(feedbackAtom);
  const [accessToken] = useRecoilState(accessTokenAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);
  const [checkExercise] = useRecoilState(checkExerciseAtom);
  const [selectedLang] = useRecoilState(selectedLangAtom);
  const [counter, setCounter] = useState(0);

  const postConversation = useCallback(
    async (selectedOption: string, userInputType?: TUserInputType) => {
      const sendConversationPayload = prepareConversationPayload(
        conversations,
        selectedOption,
        frameParams.book_code,
        userInputType
      );

      //Send the GA event
      if (sendConversationPayload.content_type === 'single_input') {
        if (!isTelemetryEnabled() && getRuntimeEnvironment() === 'prod') {
          sendInputEvent(GOOGLE_ANALYTICS.EVENTS.SEND_EVENT, conversations, frameParams);
        }
        setCounter(counter + 1);
      } else {
        if (!isTelemetryEnabled() && getRuntimeEnvironment() === 'prod')
          sendInputEvent(GOOGLE_ANALYTICS.EVENTS.CHECK_ANSWER_EVENT, conversations, frameParams);
      }

      const { data: exerciseData } = checkExercise;
      const hasMultiVariants = exerciseData?.multilingual_variants?.some(Boolean);
      if (hasMultiVariants) {
        sendConversationPayload['selectedLocale'] = selectedLang;
      }

      setConversations({
        ...conversations,
        loading: true,
      });

      const start = performance.now();
      networkService
        .post<IConversations>(
          (process.env.REACT_APP_BASE_URL ?? '') + BFF_ENDPOINTS.SEND_CONVERSATION,
          JSON.stringify(sendConversationPayload),
          {
            'X-Authorization': 'Bearer ' + accessToken,
          }
        )
        .then((response) => {
          const end = performance.now();
          // Telemetry: Output Success
          const _eventParams: any = {
            event_action: 'Output Success',
            response_duration: `${Math.trunc(end - start)}`,
            conversation_id: response.data?.id,
            locked:
              response.data?.is_open_chat_disabled ||
              response.data?.messages[response?.data.messages?.length - 1]?.locked,
            attempts_left:
              response.data?.messages[response?.data.messages?.length - 1]?.attempts_left,
            chat_action_ids: response.data?.messages?.map((message) => message.id),
            mlm_book_code: frameParams?.book_code ?? 'N/A',
            app_id: 'MLMAITutor',
            exercise_id: frameParams?.exercise_id,
            user_role: frameParams?.['user-role'],
            person_id_type: frameParams?.person_id_type,
            login_session_id: frameParams?.login_session_id,
          };

          if (response.data) {
            // translates end message into user locale language
            response.data?.messages.forEach((message) => {
              if (message.content?.includes(COPY.SUBSTRING_MESSAGE_REACHED)) {
                message.content = i18n.t('SUBSTRING_MESSAGE_REACHED');
              }
            });
            // Enabling end of conversation capture
            if (
              response.data?.messages[response?.data.messages?.length - 1]?.content.includes(
                i18n.t('SUBSTRING_MESSAGE_REACHED')
              )
            ) {
              _eventParams['conversation_end'] = true;
            }

            // Enabling end of conversation capture
            if (
              response.data?.messages[response?.data.messages?.length - 2]?.content.includes(
                i18n.t('SUBSTRING_MESSAGE_REACHED')
              )
            ) {
              _eventParams['conversation_end'] = true;
            }
          }

          if (
            response.data?.messages[response?.data.messages?.length - 1].is_correct !== undefined
          ) {
            _eventParams['correctness'] =
              response.data?.messages[response?.data.messages?.length - 1].is_correct;

            // Enabling Nudge capture
            if (response.data?.messages[response?.data.messages?.length - 1]?.nudge) {
              _eventParams['nudge'] =
                response.data?.messages[response?.data.messages?.length - 1].nudge;
            }

            // Enabling Attempts Tried capture
            if (
              response.data?.messages[response?.data.messages?.length - 1].id !==
              studentTracker.currentQuestionId
            ) {
              studentTracker.startNewQuestion(
                response.data.messages[response.data.messages.length - 1].id
              );
              studentTracker.attemptQuestion(
                response.data.messages[response.data.messages.length - 1].attempts_left
              );

              _eventParams['attempt'] = studentTracker.getAttemptsTried();
            } else {
              studentTracker.attemptQuestion(
                response.data.messages[response.data.messages.length - 1].attempts_left
              );

              _eventParams['attempt'] = studentTracker.getAttemptsTried();
            }
          } else if (
            response.data?.messages[response?.data.messages?.length - 2].is_correct !== undefined
          ) {
            _eventParams['correctness'] =
              response.data?.messages[response?.data.messages?.length - 2].is_correct;

            // Enabling Nudge capture
            if (response.data?.messages[response?.data.messages?.length - 2]?.nudge) {
              _eventParams['nudge'] =
                response.data?.messages[response?.data.messages?.length - 2].nudge;
            }

            // Enabling Attempts Tried capture
            if (
              response.data?.messages[response?.data.messages?.length - 2].id !==
              studentTracker.currentQuestionId
            ) {
              studentTracker.startNewQuestion(
                response.data.messages[response.data.messages.length - 2].id
              );
              studentTracker.attemptQuestion(
                response.data.messages[response.data.messages.length - 2].attempts_left
              );

              _eventParams['attempt'] = response.data.messages[response.data.messages.length - 2]
                .is_correct
                ? 1
                : studentTracker.getAttemptsTried();
            } else {
              studentTracker.attemptQuestion(
                response.data.messages[response.data.messages.length - 2].attempts_left
              );

              _eventParams['attempt'] = studentTracker.getAttemptsTried();
            }
          }

          captureEvent(_eventParams);

          if (
            sendConversationPayload.content_type === 'single_input' &&
            sendConversationPayload.content &&
            sendConversationPayload.content.length > 1
          ) {
            // Telemetry: Responds to AI
            captureEvent({
              event_action: 'Responds to AI',
              event_label: `${
                response.data?.messages[response?.data.messages?.length - 1].content
              }`,
              command_source_code: userInputType,
              user_input: `${sendConversationPayload.content}`,
              element_type: 'Text',
              conversation_id: conversations.data.id,
              mlm_book_code: frameParams?.book_code ?? 'N/A',
              app_id: 'MLMAITutor',
              exercise_id: frameParams?.exercise_id,
              user_role: frameParams?.['user-role'],
              person_id_type: frameParams?.person_id_type,
              login_session_id: frameParams?.login_session_id,
            });
          }

          if (response.data?.messages[response.data.messages.length - 1].is_ai_message) {
            // Telemetry: AI Response
            captureEvent({
              event_action: 'AI Response',
              event_label: `${counter + 1}`,
              element_type: 'Text',
              conversation_id: conversations.data.id,
              chat_action_ids: [...response.data.messages.map((message) => message.id)],
              mlm_book_code: frameParams?.book_code ?? 'N/A',
              app_id: 'MLMAITutor',
              exercise_id: frameParams?.exercise_id,
              user_role: frameParams?.['user-role'],
              person_id_type: frameParams?.person_id_type,
              login_session_id: frameParams?.login_session_id,
            });
          }

          if (
            response?.data?.messages?.at(-1)?.content_type === 'single_input' &&
            response?.data?.messages?.at(-1)?.content == COPY.CONVERSATION_END
          ) {
            // Telemetry: End of Questions
            captureEvent({
              event_action: 'End of Questions Triggered',
              intention: 'Office Hours',
              chat_type: 'Practise',
              conversation_id: conversations.data.id,
              mlm_book_code: frameParams?.book_code ?? 'N/A',
              app_id: 'MLMAITutor',
              exercise_id: frameParams?.exercise_id,
              user_role: frameParams?.['user-role'],
              person_id_type: frameParams?.person_id_type,
              login_session_id: frameParams?.login_session_id,
            });
          }

          if (response.status) {
            setConversations({
              loading: false,
              data: withFeedbackMapper(response.data as IConversations, feedbackData),
              error: response.error,
            });
          } else {
            setConversations({
              loading: false,
              data: {} as IConversations,
              error: response.error,
            });
          }
        });
    },
    [conversations, setConversations, accessToken, feedbackData, frameParams, counter]
  );

  return postConversation;
}

export default useSendConversation;
